import './Tips.sass';

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, InlineHeader, Input, LottieIcon } from '$uikit';
import { useTranslator } from '$hooks';
import { Api } from '$api';
import { formatInputAmount } from '$utils/number';
import { toastActions } from '$store/toast';
import { imActions } from '$store/im';
import { formatCurrency } from '$utils';
import { CurrencyEnum } from '$store/models';
import { balancePayActions } from '$store/balancePay';
import { BalancePayAction } from '$store/balancePay/interface';
import { BottomSheet } from '$uikit/BottomSheet/BottomSheet';
import { balanceActions, balanceSelector } from '$store/balance';
import { TipsProps } from '$core/Tips/Tips.interface';
import { AmountInput, Currency } from '$shared/components';
import { RootState } from '$store/rootReducer';

const MESSAGE_LENGTH_LIMIT = 200;
const AMOUNT_LIST = [5, 10, 25, 50, 100];
const MAX_AMOUNT = 100;

const TipsInner = ({ peerId, postId, currency, liveId }: TipsProps) => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const currentCurrency = currency || CurrencyEnum.TON;
  const minAmount = currentCurrency === CurrencyEnum.TON ? 0.1 : 1;

  const [tonPrice, setTonPrice] = useState(0);
  const [amount, setAmount] = useState('5');
  const [message, setMessage] = useState('');
  const [isCustomAmountMode, setCustomAmountMode] = useState(false);

  useEffect(() => {
    Api.get('/rates/ton').then((resp) => {
      const data: any = resp;
      setTonPrice(data.rate);
    });
  }, []);

  const handlePriceChange = (value: string) => {
    setAmount(formatInputAmount(value, 2));
  };

  const handleMessageChange = ({ target }) => {
    setMessage(target.value.substring(0, MESSAGE_LENGTH_LIMIT));
  };

  const handleSend = () => {
    if (+amount > 100) {
      return dispatch(
        toastActions.setToastFail(
          t('tips_amount_description_limit_error', {
            amount: formatCurrency(100, currentCurrency),
          }),
        ),
      );
    }

    if (+amount < minAmount) {
      return dispatch(
        toastActions.setToastFail(
          t('tips_amount_description', {
            amount: formatCurrency(minAmount, currentCurrency),
          }),
        ),
      );
    }

    dispatch(toastActions.setToastLoading());
    Api.post(liveId ? `/streaming/im/${liveId}/tips` : `/im/${peerId}/tips`, {
      amount: +amount,
      message: (message || '').trim(),
      postId: Number(postId),
      currency: currentCurrency,
    })
      .then((resp) => {
        const data: any = resp;
        dispatch(toastActions.hideToast());
        if (currentCurrency === CurrencyEnum.EUR) {
          dispatch(
            balancePayActions.open({
              amount: +amount,
              action: BalancePayAction.TIPS,
              objectId: peerId,
              extraId: data.tips.id,
            }),
          );
        } else {
          dispatch(imActions.sendTips(data.tips));
        }
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
      });
    handleClose();
  };

  const handleClose = () => {
    dispatch(balanceActions.setTipsData(null));
  };

  const amountTip = useMemo(() => {
    const formattedMinAmount = formatCurrency(minAmount, currentCurrency);
    const formattedMaxAmount = formatCurrency(MAX_AMOUNT, currentCurrency);

    if (+amount < minAmount) {
      return t('tips_custom_amount_small_err', {
        amount: formattedMinAmount,
      });
    } else if (+amount > MAX_AMOUNT) {
      return t('tips_custom_amount_large_err', {
        amount: formattedMaxAmount,
      });
    }

    return t('tips_custom_amount_tip', {
      minAmount: formattedMinAmount,
      maxAmount: formattedMaxAmount,
    });
  }, [amount]);

  return (
    <BottomSheet
      onClose={handleClose}
      title={t('post_send_tip')}
      className="Tips__BottomSheet"
    >
      <AmountInput
        amount={amount}
        onInputChange={handlePriceChange}
        title={t('tips_amount_title')}
        amountList={AMOUNT_LIST}
        isCustomAmountMode={isCustomAmountMode}
        onInlineButtonClick={() => setCustomAmountMode(true)}
        currency={currentCurrency}
        inputTip={amountTip}
      />
      <div className="Tips__message">
        <InlineHeader type="sub">{t('tips_message_title')}</InlineHeader>
        <Input
          value={message}
          onChange={handleMessageChange}
          multiline
          placeholder={t('tips_message_placeholder')}
        />
      </div>
      <Button className="Tips__sendButton" onClick={handleSend}>
        {t('tips_tip')} <Currency amount={+amount} currency={currentCurrency} />
      </Button>
    </BottomSheet>
  );
};

const TipsPaid = () => {
  const dispatch = useDispatch();
  const t = useTranslator();

  useEffect(() => {
    dispatch(toastActions.hideToast());
  }, []);

  const handleClose = () => {
    dispatch(balanceActions.setIsTipsPaid(false));
  };

  return (
    <BottomSheet onClose={handleClose} className="Tips__BottomSheet">
      <LottieIcon name="congratulation" />
      <div className="Tips__success__title">{t('tips_sent_success')}</div>
      <Button className="Tips__success__close" onClick={handleClose}>
        {t('tips_close')}
      </Button>
    </BottomSheet>
  );
};

export const Tips = () => {
  const { tipsData, isTipsPaid } = useSelector(balanceSelector);

  if (tipsData) return <TipsInner {...tipsData} />;

  if (isTipsPaid) return <TipsPaid />;

  return null;
};
